import { __assign, __rest } from "tslib";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useMemo, useRef, useState } from 'react';
import * as React from 'react';
import classnames from 'classnames';
import { useModalState } from '../ModalsProvider';
import MenuList from '../MenuList';
import Input from '../Input';
import EyeSlash from '../Icons/Eye/EyeSlash';
import Eye from '../Icons/Eye/Eye';
import { useForkRef } from '../utils/withForwardedRef';
import { KeyboardEventKey } from '../typings';
import { triggerChangeEvent } from '../utils/event';
import { IconButton, PopperDropDownStyled } from './TextField.styles';
var TextField = React.forwardRef(function (_a, ref) {
  var errorText = _a.errorText,
      menuItemsProp = _a.menuItems,
      DropDownProps = _a.DropDownProps,
      inputProps = __rest(_a, ["errorText", "menuItems", "DropDownProps"]);

  var type = inputProps.type,
      readOnly = inputProps.readOnly,
      disabled = inputProps.disabled,
      value = inputProps.value,
      className = inputProps.className,
      message = inputProps.message,
      error = inputProps.error,
      adornmentProp = inputProps.adornment,
      hideReadOnlyIcon = inputProps.hideReadOnlyIcon,
      onFocus = inputProps.onFocus,
      onBlur = inputProps.onBlur,
      onKeyDown = inputProps.onKeyDown,
      onChange = inputProps.onChange;
  var initialDropDownOpen = useModalState(DropDownProps === null || DropDownProps === void 0 ? void 0 : DropDownProps.id).open;

  var _b = useState(false),
      showPassword = _b[0],
      setShowPassword = _b[1];

  var _c = useState(initialDropDownOpen !== null && initialDropDownOpen !== void 0 ? initialDropDownOpen : false),
      dropDownOpen = _c[0],
      setDropDownOpen = _c[1];

  var _d = useState(0),
      menuMinWidth = _d[0],
      setMenuMinWidth = _d[1];

  var _e = useState(false),
      popoverFocused = _e[0],
      setPopoverFocused = _e[1];

  var _f = useState(-1),
      focusedItemIndex = _f[0],
      setFocusedItemIndex = _f[1];

  var showLockIcon = readOnly && !hideReadOnlyIcon;
  var showDropDown = dropDownOpen && !disabled;
  var paperRef = useRef(null);
  var inputRef = useRef(null);
  var popoverClickRef = useRef(false);
  var itemsCountRef = useRef(menuItemsProp ? React.Children.count(menuItemsProp) : 0);
  var focusedItemValueRef = useRef(undefined);
  var handleRef = useForkRef(inputRef, ref);

  var isCursorAtEnd = function isCursorAtEnd() {
    var input = inputRef.current;
    return Boolean(input && input.selectionStart === input.selectionEnd && input.selectionStart === input.value.length);
  };

  var openDropDown = function openDropDown() {
    var _a;

    setDropDownOpen(true);
    setMenuMinWidth(((_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.clientWidth) || 0);
    setPopoverFocused(false);
  };

  var handleDropDownClose = useCallback(function () {
    if (showDropDown) {
      setDropDownOpen(false);
      setFocusedItemIndex(-1);
      setPopoverFocused(false);
    }
  }, [showDropDown]);
  var handleFocus = useCallback(function (event) {
    if (menuItemsProp) {
      if (showDropDown) {
        setPopoverFocused(false);
      } else {
        openDropDown();
      }
    }

    if (onFocus) {
      onFocus(event);
    }
  }, [showDropDown, menuItemsProp, onFocus]);
  var handleBlur = useCallback(function (event) {
    if (menuItemsProp && showDropDown) {
      if (popoverClickRef.current) {
        setPopoverFocused(true);
      } else {
        setDropDownOpen(false);
        setFocusedItemIndex(-1);
      }
    }

    if (onBlur) {
      onBlur(event);
    }
  }, [menuItemsProp, showDropDown, onBlur]);
  var handleChange = useCallback(function (e) {
    onChange === null || onChange === void 0 ? void 0 : onChange(e);

    if (menuItemsProp && !showDropDown) {
      openDropDown();
    }
  }, [showDropDown, menuItemsProp, onChange]);
  var handleDropDownClickStart = useCallback(function () {
    popoverClickRef.current = true;
  }, []);
  var handleDropDownClickEnd = useCallback(function () {
    popoverClickRef.current = false;
  }, []);

  var handleItemClick = function handleItemClick(itemProps) {
    return function (event) {
      if (itemProps.onClick) {
        itemProps.onClick(event);
      }

      if (onChange && inputRef.current) {
        triggerChangeEvent(inputRef.current, itemProps.value);
      }
    };
  };

  var handleKeyDown = useCallback(function (event) {
    if (event.defaultPrevented || !menuItemsProp) {
      return;
    }

    switch (event.key) {
      case KeyboardEventKey.ARROW_UP:
      case KeyboardEventKey.UP:
        if (showDropDown) {
          event.preventDefault();
          setFocusedItemIndex(function (prevIndex) {
            return prevIndex > 0 ? prevIndex - 1 : itemsCountRef.current - 1;
          });
        }

        break;

      case KeyboardEventKey.ARROW_DOWN:
      case KeyboardEventKey.DOWN:
        if (showDropDown || isCursorAtEnd()) {
          event.preventDefault();

          if (showDropDown) {
            setFocusedItemIndex(function (prevIndex) {
              return prevIndex < itemsCountRef.current - 1 ? prevIndex + 1 : 0;
            });
          } else {
            openDropDown();
          }
        }

        break;

      case KeyboardEventKey.ENTER:
        if (focusedItemValueRef.current != null) {
          event.preventDefault();
          handleDropDownClose();

          if (onChange && inputRef.current) {
            triggerChangeEvent(inputRef.current, focusedItemValueRef.current);
          }
        }

        break;
    }
  }, [menuItemsProp, showDropDown, handleDropDownClose, onChange]);
  var handleInputKeyDown = useCallback(function (event) {
    if (onKeyDown) {
      onKeyDown(event);
    }

    handleKeyDown(event);
  }, [onKeyDown, handleKeyDown]);
  var handleDropDownKeyDown = useCallback(function (event) {
    if (DropDownProps && DropDownProps.onKeyDown) {
      DropDownProps.onKeyDown(event);
    }

    handleKeyDown(event);
  }, [DropDownProps, handleKeyDown]);
  var togglePasswordVisibility = useCallback(function () {
    setShowPassword(function (prevState) {
      return !prevState;
    });
  }, []);
  var adornment = useMemo(function () {
    if (showLockIcon) {
      return undefined;
    }

    if (type === 'password') {
      return _jsx(IconButton, __assign({
        onClick: togglePasswordVisibility,
        disabled: disabled,
        tabIndex: -1
      }, {
        children: showPassword ? _jsx(Eye, {}) : _jsx(EyeSlash, {})
      }));
    }

    return adornmentProp;
  }, [showLockIcon, type, disabled, showPassword, adornmentProp, togglePasswordVisibility]);
  focusedItemValueRef.current = undefined;
  var itemIndex = 0;
  var menuItems = menuItemsProp && React.Children.map(menuItemsProp, function (child) {
    if (!React.isValidElement(child)) {
      return null;
    }

    var childProps = __assign(__assign({}, child.props), {
      onClick: child.props.disabled ? undefined : handleItemClick(child.props),
      selected: child.props.selected !== undefined ? child.props.selected : child.props.value === value,
      focused: child.props.focused == null && !child.props.disabled ? focusedItemIndex === itemIndex : child.props.focused,
      paperRef: paperRef
    });

    if (showDropDown && childProps.focused) {
      focusedItemValueRef.current = child.props.value;
    }

    if (!child.props.disabled) {
      itemIndex += 1;
    }

    return _jsx(child.type, __assign({
      autoScroll: focusedItemIndex < 0 ? childProps.selected : false
    }, childProps));
  });
  itemsCountRef.current = itemIndex;
  var inputClassName = classnames(className, {
    popoverFocused: popoverFocused
  });
  return _jsxs(_Fragment, {
    children: [_jsx(Input, __assign({}, inputProps, {
      className: inputClassName,
      type: type === 'password' && showPassword ? 'text' : type,
      error: error || Boolean(errorText),
      message: errorText || message,
      ref: handleRef,
      onChange: handleChange,
      autoComplete: menuItems && menuItems.length ? 'off' : inputProps.autoComplete,
      adornment: adornment,
      onFocus: handleFocus,
      onBlur: handleBlur,
      onKeyDown: handleInputKeyDown
    })), menuItems && _jsx(PopperDropDownStyled, __assign({
      anchorEl: inputRef.current || undefined,
      open: showDropDown,
      onClose: handleDropDownClose,
      active: popoverFocused,
      onPaperClickStart: handleDropDownClickStart,
      onPaperClickEnd: handleDropDownClickEnd,
      minWidth: menuMinWidth,
      paperClassName: "menu-wrap",
      paperRef: paperRef
    }, DropDownProps, {
      onKeyDown: handleDropDownKeyDown
    }, {
      children: _jsx(MenuList, __assign({
        onClick: handleDropDownClose
      }, {
        children: menuItems
      }))
    }))]
  });
});
export default React.memo(TextField);